/**
 * Retouch screen (retouch tool).
 *
 * Flow: 1 Upload photo → 2 Choose one or more touch-ups → 3 Strength → Generate.
 * The touch-ups are product features, not templates, and they run together in
 * ONE job for one price. The server owns the wording (skin keeps its texture,
 * freckles and moles stay, nothing reshapes the face), so the app only sends
 * `options.retouchTypes` and `options.intensity`.
 * The price is data (GET /credits/pricing).
 */
import { ApiError, api } from '../api/client.js';
import { navigate } from '../router.js';
import { escapeHtml } from '../components/placeholder.js';
import { icon } from '../components/icon.js';
import { toast, toastApiError } from '../ui/toast.js';
import { creditsChanged, getBalance, refreshCredits } from '../state/credits.js';
import { requireAccount, requireSignIn } from '../state/gate.js';
import { withUploadGuide } from '../components/uploadGuide.js';
import { SAMPLE_PHOTOS } from '../content/samplePhotos.js';
import type { CreditPricingConfig, JobOptions, RetouchType, UploadFileMeta } from '@loomia/shared';

const TOOL = 'retouch' as const;
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const CLIENT_MAX_BYTES = 25 * 1024 * 1024;

const TYPES: ReadonlyArray<{ id: RetouchType; label: string; icon: string }> = [
  { id: 'skin_smooth', label: 'Skin Smooth', icon: 'face_retouching_natural' },
  { id: 'blemish_fix', label: 'Blemish Fix', icon: 'healing' },
  { id: 'teeth_whiten', label: 'Teeth Whiten', icon: 'dentistry' },
  { id: 'eye_brighten', label: 'Eye Brighten', icon: 'visibility' },
];

/** One tap: the three everyday touch-ups together. */
const AUTO: readonly RetouchType[] = ['skin_smooth', 'blemish_fix', 'eye_brighten'];

const STRENGTHS: ReadonlyArray<{ id: 'subtle' | 'natural' | 'strong'; label: string }> = [
  { id: 'subtle', label: 'Subtle' },
  { id: 'natural', label: 'Natural' },
  { id: 'strong', label: 'Strong' },
];

interface Photo {
  status: 'empty' | 'uploading' | 'ready' | 'error';
  progress: number;
  previewUrl: string | null;
  fileId: string | null;
  bytes: number;
  error: string | null;
}

let renderSeq = 0;
let prevCleanup: (() => void) | null = null;

function newId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `id-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
}

export function renderRetouch(root: HTMLElement, _query: URLSearchParams): void {
  const seq = ++renderSeq;
  const alive = (): boolean => seq === renderSeq && root.isConnected;

  const load = async (): Promise<void> => {
    if (prevCleanup) {
      prevCleanup();
      prevCleanup = null;
    }
    root.innerHTML = '<div class="co"><div class="co-skel"></div></div>';
    const [pricingR] = await Promise.allSettled([api.getPricing(), refreshCredits().catch(() => null)]);
    if (!alive()) return;
    if (pricingR.status === 'rejected') {
      root.innerHTML = `
        <div class="co"><div class="co-state">${icon('cloud_off')}
          <p>Couldn't load Retouch. Check your connection.</p>
          <button class="btn btn-secondary" data-act="retry">Try again</button></div></div>`;
      root.querySelector('[data-act="retry"]')?.addEventListener('click', () => void load());
      return;
    }
    mount(pricingR.value);
  };

  const mount = (pricing: CreditPricingConfig): void => {
    const selected = new Set<RetouchType>(['skin_smooth']);
    let intensity: 'subtle' | 'natural' | 'strong' = 'natural';
    let generating = false;
    let photo: Photo = { status: 'empty', progress: 0, previewUrl: null, fileId: null, bytes: 0, error: null };

    const price = (): number => pricing.perTool[TOOL]?.credits ?? 0;

    const missing = (): string | null => {
      if (photo.status === 'uploading') return 'Uploading…';
      if (photo.status !== 'ready') return 'Add your photo first';
      if (selected.size === 0) return 'Choose at least one retouch';
      return null;
    };

    const options = (): JobOptions => ({
      retouchTypes: TYPES.filter((t) => selected.has(t.id)).map((t) => t.id),
      intensity,
    });

    // -- Markup -----------------------------------------------------------

    const step = (n: number, title: string): string =>
      `<div class="co-step"><span class="co-num">${n}</span><h2>${title}</h2></div>`;

    const photoHtml = (): string => {
      const samples =
        SAMPLE_PHOTOS.length === 0
          ? ''
          : `<div class="co-samples"><p class="co-sub">Try these photos</p><div class="co-samples-row">${SAMPLE_PHOTOS.map(
              (s, i) => `<button class="co-sample" data-sample="${i}" aria-label="${escapeHtml(s.label)}"><img src="${escapeHtml(s.url)}" alt="" loading="lazy" /></button>`,
            ).join('')}</div></div>`;
      const rowCls = `co-photo-row${SAMPLE_PHOTOS.length === 0 ? ' no-samples' : ''}`;
      if (photo.status === 'empty') {
        return `
          <div class="${rowCls}">
            <button class="co-drop" data-act="pick">
              ${icon('add_photo_alternate')}<strong>Add your photo</strong><span>JPG, PNG or WebP · up to 25 MB</span>
            </button>
            ${samples}
          </div>`;
      }
      const label =
        photo.status === 'uploading'
          ? `Uploading… ${Math.round(photo.progress * 100)}%`
          : photo.status === 'error'
            ? escapeHtml(photo.error ?? 'Upload failed')
            : '';
      return `
        <div class="${rowCls}">
          <div class="co-photo is-${photo.status}">
            ${photo.previewUrl ? `<img src="${escapeHtml(photo.previewUrl)}" alt="Your photo" />` : ''}
            ${photo.status === 'uploading' ? `<div class="co-bar"><span style="width:${Math.round(photo.progress * 100)}%"></span></div>` : ''}
            <div class="co-photo-side">
              ${label ? `<span class="co-photo-label" data-photo-label>${label}</span>` : ''}
              <button class="co-link" data-act="pick">${photo.status === 'error' ? 'Try again' : 'Change'}</button>
            </div>
          </div>
          ${samples}
        </div>`;
    };

    const isAuto = (): boolean => selected.size === AUTO.length && AUTO.every((t) => selected.has(t));

    const typesHtml = (): string => `
      <div class="co-types co-types-5" role="group" aria-label="Retouch types">
        <button class="co-type${isAuto() ? ' selected' : ''}" role="checkbox" aria-checked="${isAuto()}" data-auto="1">
          <span class="co-type-ico">${icon('auto_awesome')}</span><span>Auto Retouch</span>
        </button>
        ${TYPES.map(
          (t) => `
          <button class="co-type${selected.has(t.id) ? ' selected' : ''}" role="checkbox" aria-checked="${selected.has(t.id)}" data-type="${t.id}">
            <span class="co-type-ico">${icon(t.icon)}</span><span>${escapeHtml(t.label)}</span>
          </button>`,
        ).join('')}
      </div>
      <p class="co-sub co-type-hint">Pick one or more. They run together in one go.${
        selected.has('teeth_whiten') ? ' Teeth Whiten needs visible teeth.' : ''
      }</p>`;

    const strengthHtml = (): string => `
      <div class="co-seg" role="radiogroup" aria-label="Retouch intensity">
        ${STRENGTHS.map(
          (s) => `<button class="co-seg-opt${s.id === intensity ? ' selected' : ''}" role="radio" aria-checked="${s.id === intensity}" data-intensity="${s.id}">${s.label}</button>`,
        ).join('')}
      </div>`;

    const footerHtml = (): string => {
      const c = price();
      const bal = getBalance();
      const short = bal !== null && bal < c ? c - bal : 0;
      const why = missing();
      const btn =
        short > 0 && !why
          ? '<button class="btn btn-primary btn-block" data-act="buy">Get more credits</button>'
          : `<button class="btn btn-primary btn-block" data-act="generate" ${generating ? 'disabled' : ''}>${
              generating ? 'Starting…' : why ? escapeHtml(why) : `Retouch ${icon('toll', { fill: true })} ${c}`
            }</button>`;
      return `
        <div class="co-total${short > 0 ? ' is-short' : ''}">
          <span>Total</span><strong>${icon('toll', { fill: true })} ${c}</strong>
          <small>Failed runs are refunded automatically</small>
        </div>
        ${short > 0 ? `<p class="co-warn">You need ${icon('toll', { fill: true })} ${short} more credits</p>` : ''}
        ${btn}`;
    };

    const paint = (): void => {
      root.innerHTML = `
        <div class="co">
          <header class="co-head">
            <button class="co-back" data-act="back" aria-label="Back">${icon('arrow_back')}</button>
            <div class="co-head-text"><h1>Retouch</h1><p>Refine portraits with natural-looking AI touch-ups.</p></div>
            <a class="co-credits" href="#/paywall" aria-label="Get credits">${icon('toll', { fill: true })} ${getBalance() ?? '–'} <span>+</span></a>
          </header>
          ${step(1, 'Upload photo')}
          ${photoHtml()}
          <p class="co-sub">Start with a clear portrait or selfie.</p>
          <p class="co-note">${icon('info')}Only use photos of yourself or people who agreed.</p>
          ${step(2, 'Choose retouch types')}
          ${typesHtml()}
          ${step(3, 'Retouch intensity')}
          ${strengthHtml()}
          <footer class="co-foot" id="co-foot">${footerHtml()}</footer>
          <input type="file" id="co-file" accept="${ACCEPTED_TYPES.join(',')}" hidden />
        </div>`;
    };

    const paintFooter = (): void => {
      const f = root.querySelector<HTMLElement>('#co-foot');
      if (f) f.innerHTML = footerHtml();
    };

    // -- Upload -----------------------------------------------------------

    const upload = async (file: File): Promise<void> => {
      if (!requireSignIn('Sign in to add your photo.')) return;
      if (!ACCEPTED_TYPES.includes(file.type)) {
        toast('Please choose a JPEG, PNG or WebP image.');
        return;
      }
      if (file.size > CLIENT_MAX_BYTES) {
        toast('That image is too large. Please pick one under 25 MB.');
        return;
      }
      if (photo.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(photo.previewUrl);
      photo = { status: 'uploading', progress: 0, previewUrl: URL.createObjectURL(file), fileId: null, bytes: file.size, error: null };
      const mine = photo;
      paint();
      try {
        const req = await api.requestUpload({
          purpose: 'generation_input',
          contentType: file.type,
          contentLength: file.size,
        });
        await api.putUploadBytes(req.uploadUrl, file, req.uploadHeaders, (r) => {
          if (!alive() || photo !== mine) return;
          mine.progress = r;
          const bar = root.querySelector<HTMLElement>('.co-bar span');
          if (bar) bar.style.width = `${Math.round(r * 100)}%`;
          const label = root.querySelector<HTMLElement>('[data-photo-label]');
          if (label) label.textContent = `Uploading… ${Math.round(r * 100)}%`;
        });
        const meta: UploadFileMeta = await api.completeUpload(req.fileId);
        if (!alive() || photo !== mine) return;
        if (meta.status !== 'ready') throw new Error(meta.error || 'Image processing failed.');
        mine.status = 'ready';
        mine.fileId = meta.id;
        mine.previewUrl = meta.thumbnails?.thumb256 ?? meta.url ?? mine.previewUrl;
      } catch (err) {
        if (!alive() || photo !== mine) return;
        mine.status = 'error';
        mine.error = err instanceof Error ? err.message : 'Upload failed.';
        toastApiError(err, 'Upload failed. Please try again.');
      }
      paint();
    };

    // -- Generate ---------------------------------------------------------

    const generate = async (): Promise<void> => {
      if (generating) return;
      if (!requireAccount('Sign in to generate.')) return;
      const why = missing();
      if (why) {
        toast(why);
        // No photo yet: open the picker right away instead of only saying so.
        if (/^Add /.test(why)) root.querySelector<HTMLElement>('[data-act="pick"]')?.click();
        return;
      }
      const c = price();
      let bal = getBalance();
      if (bal === null) bal = await refreshCredits().catch(() => null);
      if (!alive()) return;
      if (bal !== null && bal < c) {
        navigate('/paywall');
        return;
      }
      generating = true;
      paintFooter();
      try {
        const { job } = await api.submitJob({
          tool: TOOL,
          inputFileIds: photo.fileId ? [photo.fileId] : [],
          options: options(),
          idempotencyKey: newId(),
        });
        void refreshCredits().catch(() => undefined);
        navigate(`/result/${job.id}`);
      } catch (err) {
        if (!alive()) return;
        if (err instanceof ApiError && (err.status === 402 || /insufficient|credit/i.test(err.code))) {
          toast('Not enough credits.');
          navigate('/paywall');
        } else {
          toastApiError(err, 'Could not start generation. Please try again.');
        }
      } finally {
        if (alive()) {
          generating = false;
          paintFooter();
        }
      }
    };

    // -- Events (delegated) -----------------------------------------------

    root.onclick = (e) => {
      const el = (e.target as HTMLElement).closest<HTMLElement>('[data-act],[data-type],[data-auto],[data-intensity],[data-sample]');
      if (!el) return;
      const d = el.dataset;
      if (d.act === 'back') {
        if (history.length > 1) history.back();
        else navigate('/');
      } else if (d.act === 'pick') {
        if (requireSignIn('Sign in to add your photo.')) {
          withUploadGuide('retouch', () => root.querySelector<HTMLInputElement>('#co-file')?.click());
        }
      } else if (d.act === 'generate') void generate();
      else if (d.act === 'buy') navigate('/paywall');
      else if (d.auto) {
        selected.clear();
        for (const t of AUTO) selected.add(t);
        paint();
      } else if (d.type) {
        const t = TYPES.find((x) => x.id === d.type)?.id;
        if (t) {
          if (selected.has(t)) selected.delete(t);
          else selected.add(t);
          paint();
        }
      } else if (d.intensity) {
        intensity = STRENGTHS.find((x) => x.id === d.intensity)?.id ?? intensity;
        paint();
      } else if (d.sample) {
        const s = SAMPLE_PHOTOS[Number(d.sample)];
        if (s) {
          void fetch(s.url)
            .then((r) => r.blob())
            .then((b) => upload(new File([b], 'sample.webp', { type: b.type || 'image/webp' })))
            .catch(() => toast("Couldn't load that sample photo."));
        }
      }
    };

    root.onchange = (e) => {
      const input = e.target as HTMLInputElement;
      if (input.id !== 'co-file') return;
      const f = input.files?.[0];
      input.value = '';
      if (f) void upload(f);
    };

    const unsub = creditsChanged.on(() => {
      if (alive()) paintFooter();
    });
    prevCleanup = () => {
      unsub();
      if (photo.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(photo.previewUrl);
    };

    paint();
  };

  void load();
}
